import React from 'react';
import { Navigate } from 'react-router-dom';
import { useTenantFeatures } from '../../hooks/useTenantFeatures';

interface ModuleRouteProps {
  /** PlatformModule enum value (e.g., 'CORE_BANKING', 'BENEFITS') */
  moduleId: string;
  /** Optional granular feature key (e.g., 'pixEnabled', 'tedEnabled') */
  featureKey?: string;
  /** Route to redirect to if module/feature is disabled */
  redirectTo?: string;
  /** The child components to render if module is enabled */
  children: React.ReactNode;
}

/**
 * Route wrapper that blocks access if a module or specific feature is disabled.
 * This prevents users from accessing pages via direct URL when the module is off.
 * 
 * @example
 * // Block entire route if CORE_BANKING is disabled
 * <ModuleRoute moduleId="CORE_BANKING">
 *   <BankingPage />
 * </ModuleRoute>
 * 
 * @example
 * // Block route if PIX specifically is disabled
 * <ModuleRoute moduleId="CORE_BANKING" featureKey="pixEnabled">
 *   <PixPage />
 * </ModuleRoute>
 */
export function ModuleRoute({
  moduleId,
  featureKey,
  redirectTo = '/dashboard',
  children,
}: ModuleRouteProps) {
  const { isFeatureEnabled, isLoading } = useTenantFeatures();

  // While loading, show nothing (avoids flash of redirect)
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        color: 'var(--text-secondary, #888)',
      }}>
        <div className="loading-spinner" />
      </div>
    );
  }

  // Check if feature is enabled
  const enabled = isFeatureEnabled(moduleId, featureKey);

  if (!enabled) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}

export default ModuleRoute;
